import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import type { z } from 'zod/v4';
import { PostalSendingOptionsSchema } from './use-load-postal-sending-options';

const URL = '/postal_sending_options';

const UpdatePostalSendingOptionsVariablesSchema = PostalSendingOptionsSchema.partial();

export type UpdatePostalSendingOptionsVariables = z.infer<
  typeof UpdatePostalSendingOptionsVariablesSchema
>;

export async function updatePostalSendingOptionsFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdatePostalSendingOptionsVariables,
) {
  const instance = await axiosInstance;

  return instance.put(URL, UpdatePostalSendingOptionsVariablesSchema.parse(variables));
}

export function useUpdatePostalSendingOptions() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdatePostalSendingOptionsVariables>({
    mutationFn: variables => updatePostalSendingOptionsFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['postalSendingOptions'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdatePostalSendingOptionsVariables,
    'updatePostalSendingOptions'
  >('updatePostalSendingOptions', mutation);
}
